import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  role: string;
  roles: string[];
  clinic_id?: number;
  specialty?: string;
  // ADDED: New customization fields
  doctor_name_arabic?: string;
  specializations_arabic?: string;
  specializations_english?: string;
  custom_footer?: string;
  // ADDED: New secretary-related properties
  is_main_secretary?: boolean;
  assigned_doctor_id?: number | null;
  assigned_doctor?: {
    id: number;
    first_name: string;
    last_name: string;
    specialty?: string;
  } | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  // ADDED: Helper methods for role checking
  hasRole: (role: string) => boolean;
  isAdmin: () => boolean;
  isDoctor: () => boolean;
  isSecretary: () => boolean;
  isMainSecretary: () => boolean;
  isRegularSecretary: () => boolean;
  canAccessDoctor: (doctorId: number) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const API_URL =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  useEffect(() => {
    // Check for existing token on app start
    const savedToken = localStorage.getItem("auth_token");

    if (savedToken) {
      setToken(savedToken);
      fetchUser(savedToken);
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchUser = async (authToken: string) => {
    try {
      const response = await fetch(`${API_URL}/user`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          Accept: "application/json",
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        // Token is invalid, remove it
        localStorage.removeItem("auth_token");
        setToken(null);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
      localStorage.removeItem("auth_token");
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);

      // First get CSRF cookie - use the same API_URL
      await fetch(`${API_URL}/sanctum/csrf-cookie`, {
        method: "GET",
        credentials: "include",
      });

      const response = await fetch(`${API_URL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest", // Add this for Laravel
        },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        const authToken = data.token;

        setToken(authToken);
        setUser(data.user);
        localStorage.setItem("auth_token", authToken);

        return true;
      } else {
        const errorData = await response.json();
        console.error("Login failed:", response.status, errorData);
        return false;
      }
    } catch (error) {
      console.error("Login error:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (token) {
        await fetch(`${API_URL}/logout`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem("auth_token");
    }
  };

  // ADDED: Helper methods for role checking
  const hasRole = (role: string): boolean => {
    return user?.roles?.includes(role) || user?.role === role || false;
  };

  const isAdmin = (): boolean => {
    return hasRole("admin");
  };

  const isDoctor = (): boolean => {
    return hasRole("doctor");
  };

  const isSecretary = (): boolean => {
    return hasRole("secretary");
  };

  const isMainSecretary = (): boolean => {
    return isSecretary() && (user?.is_main_secretary || false);
  };

  const isRegularSecretary = (): boolean => {
    return isSecretary() && !isMainSecretary() && !!user?.assigned_doctor_id;
  };

  const canAccessDoctor = (doctorId: number): boolean => {
    if (isAdmin() || isMainSecretary()) {
      return true; // Admin and main secretary can access all doctors
    }

    if (isDoctor()) {
      return user?.id === doctorId; // Doctor can only access their own data
    }

    if (isRegularSecretary()) {
      return user?.assigned_doctor_id === doctorId; // Regular secretary can only access assigned doctor
    }

    return false;
  };

  const value = {
    user,
    token,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    // ADDED: Expose helper methods
    hasRole,
    isAdmin,
    isDoctor,
    isSecretary,
    isMainSecretary,
    isRegularSecretary,
    canAccessDoctor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
